import logger from '@/lib/logging';

export interface CompressedImageMeta {
  dataUrl: string;
  originalWidth: number;
  originalHeight: number;
  width: number;
  height: number;
  /** Uniform scale applied to width/height (new / original). */
  scale: number;
}

/**
 * Compresses an image given its data URL or File object.
 *
 * @param source - The image data URL or File to compress
 * @param maxDim - Maximum dimension (width or height) to resize to
 * @param quality - JPEG compression quality (0 to 1)
 * @returns Promise resolving to a compressed base64 JPEG data URL
 */
export async function compressImage(
  source: string | File,
  maxDim = 800,
  quality = 0.6,
): Promise<string> {
  const meta = await compressImageWithMeta(source, maxDim, quality);
  return meta.dataUrl;
}

/**
 * Like {@link compressImage}, but also returns original/new dimensions and scale
 * so callers can keep pixel-space landmarks in sync with the resized image.
 */
export async function compressImageWithMeta(
  source: string | File,
  maxDim = 800,
  quality = 0.6,
): Promise<CompressedImageMeta> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const originalWidth = img.width;
      const originalHeight = img.height;
      let { width, height } = img;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > height) {
        if (width > maxDim) {
          height = Math.round(height * (maxDim / width));
          width = maxDim;
        }
      } else {
        if (height > maxDim) {
          width = Math.round(width * (maxDim / height));
          height = maxDim;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return reject(new Error('Could not get canvas context'));
      }

      ctx.drawImage(img, 0, 0, width, height);

      const prevSize = typeof source === 'string' ? source.length : source.size;
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      const newSize = dataUrl.length;
      const scale = originalWidth > 0 ? width / originalWidth : 1;

      logger.debug('Image compressed', {
        category: 'ui',
        originalSize: (prevSize / 1024).toFixed(1) + ' KB',
        newSize: (newSize / 1024).toFixed(1) + ' KB',
        reduction: (((prevSize - newSize) / prevSize) * 100).toFixed(1) + '%',
        newDimensions: `${width}x${height}`,
        scale,
      });

      resolve({
        dataUrl,
        originalWidth,
        originalHeight,
        width,
        height,
        scale,
      });
    };

    img.onerror = (err) => {
      logger.error('Error loading image for compression', { error: err });
      reject(new Error('Failed to load image for compression'));
    };

    if (typeof source === 'string') {
      img.src = source;
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(source);
    }
  });
}
